import {
  CloudUploadOutlined,
  PlusOutlined,
  StarFilled,
  StarOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Button, Drawer, Input, Modal, Typography, message } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { RetailerMailMessage } from "../../services/retailerApi";

export type ThreadDrawerHotMemoryItem = {
  id: string;
  type: "emailMessage";
  label?: string;
  data?: Record<string, any>;
};

interface ThreadDrawerProps {
  open: boolean;
  activeConversationId: string | null;
  emails: RetailerMailMessage[];
  currentUserEmail?: string;
  collaboratorEmails?: string[];
  onClose: () => void;
  onSummarizeMessage?: (message: RetailerMailMessage) => void;
  summaryLoadingById?: Record<string, boolean>;
  onGenerateDraftReply?: (payload: {
    latestMessage: RetailerMailMessage;
    threadMessages: RetailerMailMessage[];
    styleSamples: string[];
  }) => Promise<string | undefined>;
  isGeneratingDraft?: boolean;
  messageDraft: string;
  onMessageDraftChange: (value: string) => void;
  onUpdateMessage?: (messageId: string, updates: Partial<RetailerMailMessage>) => void;
  onToggleImportant?: (messageId: string, nextImportant: boolean) => Promise<void>;
  onSummarizeThread?: (threadId: string) => Promise<string | undefined>;
}

const ThreadDrawer: React.FC<ThreadDrawerProps> = ({
  open,
  activeConversationId,
  emails,
  currentUserEmail = "",
  collaboratorEmails = [],
  onClose,
  onSummarizeMessage,
  summaryLoadingById = {},
  onGenerateDraftReply,
  isGeneratingDraft = false,
  messageDraft,
  onMessageDraftChange,
  onUpdateMessage,
  onToggleImportant,
  onSummarizeThread,
}) => {
  if (!activeConversationId) return null;

  const [isThreadSummaryModalOpen, setIsThreadSummaryModalOpen] = useState(false);
  const requestedThreadRef = useRef<string | null>(null);

  const normalizedCurrentUserEmail = currentUserEmail.toLowerCase();
  const normalizedCollaborators = collaboratorEmails.map((email) => email.toLowerCase());

  const messages = emails
    .filter((m) => m.conversationId === activeConversationId || m.id === activeConversationId)
    .sort((a, b) => new Date(a.receivedDateTime || 0).getTime() - new Date(b.receivedDateTime || 0).getTime());

  const firstMsg = messages[0];
  const latestMsg = messages[messages.length - 1];
  const threadSummary = messages
    .map((msg) => msg.threadSummary)
    .find((value) => typeof value === "string" && value.trim().length > 0);

  useEffect(() => {
    if (!open || !activeConversationId || !onSummarizeThread) return;
    if (threadSummary) return;
    if (requestedThreadRef.current === activeConversationId) return;
    requestedThreadRef.current = activeConversationId;

    const run = async () => {
      try {
        const summary = await onSummarizeThread(activeConversationId);
        if (!summary) return;
        messages.forEach((msg) => {
          if (msg.id) {
            onUpdateMessage?.(msg.id, { threadSummary: summary });
          }
        });
      } catch (error) {
        // noop
      }
    };
    void run();
  }, [activeConversationId, onSummarizeThread, onUpdateMessage, open, threadSummary, messages]);

  const handleToggleImportant = async (msg: RetailerMailMessage) => {
    if (!msg?.id || !onToggleImportant) return;
    const nextImportant = !msg.isImportant;
    onUpdateMessage?.(msg.id, { isImportant: nextImportant });
    try {
      await onToggleImportant(msg.id, nextImportant);
    } catch (error) {
      onUpdateMessage?.(msg.id, { isImportant: !nextImportant });
      message.error("Erreur lors du flag important");
    }
  };

  const styleSamples = messages
    .filter((msg) => msg.mailboxIdentity && msg.from && msg.mailboxIdentity === msg.from)
    .map((msg) => msg.preview)
    .filter((value): value is string => typeof value === "string" && value.trim().length > 0)
    .slice(-3);

  return (
    <Drawer
      title={
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <Typography.Text strong style={{ fontSize: 14 }}>
            {firstMsg?.subject || "(Sans objet)"}
          </Typography.Text>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              {messages.length} messages dans cette conversation
            </Typography.Text>
            {threadSummary && (
              <>
                <Typography.Paragraph
                  type="secondary"
                  style={{ fontSize: 11, margin: 0, flex: 1 }}
                  ellipsis={{ rows: 1 }}
                >
                  {threadSummary}
                </Typography.Paragraph>
                <Button size="small" type="link" onClick={() => setIsThreadSummaryModalOpen(true)}>
                  Voir plus
                </Button>
              </>
            )}
          </div>
        </div>
      }
      placement="right"
      width={520}
      onClose={onClose}
      open={open}
      className="messaging-drawer"
    >
      <div className="messages-container">
        {messages.map((msg, idx) => {
          const fromLower = (msg.from || "").toLowerCase();
          const isSelf = Boolean(normalizedCurrentUserEmail && fromLower === normalizedCurrentUserEmail);
          const isCollaborator = !isSelf && normalizedCollaborators.includes(fromLower);
          const bubbleClass = isSelf
            ? "outbound self"
            : isCollaborator
            ? "outbound collaborator"
            : "inbound external";
          const messageSummary = msg.aiSummary || msg.summary || "";
          const messageId = msg.id;
          const messageBubble = (
            <div className={`message-bubble ${bubbleClass}`}>
              <div className="message-meta">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 600 }}>{msg.from?.split("@")[0]}</span>
                  <span
                    className={`message-pill ${isSelf ? "self" : isCollaborator ? "collab" : "external"}`}
                  >
                    {isSelf ? "Moi" : isCollaborator ? "Collaborateur" : "Interlocuteur"}
                  </span>
                  {onToggleImportant && (
                    <Button
                      type="text"
                      size="small"
                      onClick={() => handleToggleImportant(msg)}
                      icon={msg.isImportant ? <StarFilled style={{ color: "#f59e0b" }} /> : <StarOutlined />}
                      style={{ padding: 0 }}
                    />
                  )}
                </div>
                <span>{dayjs(msg.receivedDateTime).format("DD MMM, HH:mm")}</span>
              </div>
              {messageSummary ? null : (
                onSummarizeMessage && (
                  <Button
                    size="small"
                    type="link"
                    loading={summaryLoadingById[messageId || ""]}
                    onClick={() => messageId && onSummarizeMessage(msg)}
                  >
                    Générer un résumé
                  </Button>
                )
              )}
              <div className="message-content">
                <div className="message-content-summary" style={{ whiteSpace: "pre-line" }}>
                  {messageSummary || msg.preview || ""}
                </div>
                <div className="message-content-body" style={{ whiteSpace: "pre-line" }}>
                  {msg.preview || ""}
                </div>
              </div>
              {msg.attachments && msg.attachments.length > 0 && (
                <div
                  className="message-attachments"
                  style={{ display: "flex", gap: 8, overflowX: "auto", overflowY: "hidden", paddingBottom: 4 }}
                >
                  {msg.attachments.map((att) => (
                    <div
                      key={att.id}
                      className="message-attachment"
                      onClick={() => att.downloadUrl && window.open(att.downloadUrl, "_blank")}
                      style={{ flex: "0 0 auto" }}
                    >
                      <CloudUploadOutlined />
                      <span style={{ fontSize: 11 }}>{att.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );

          return (
            <div key={msg.id || idx} style={{ marginBottom: 24 }}>
              {messageBubble}
            </div>
          );
        })}
      </div>
      <div className="messaging-input-area">
        <div className={`messaging-input-wrapper ${isGeneratingDraft ? "ai-generating" : ""}`}>
          <Input.TextArea
            placeholder="Écrire une réponse..."
            autoSize={{ minRows: 2, maxRows: 6 }}
            style={{ borderRadius: 12, border: "1px solid #e2e8f0" }}
            value={messageDraft}
            onChange={(event) => onMessageDraftChange(event.target.value)}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12, gap: 8 }}>
          <Button
            icon={<ThunderboltOutlined />}
            style={{
              borderRadius: 8,
              borderColor: "#c7d2fe",
              background: "#eef2ff",
              color: "#4338ca",
            }}
            loading={isGeneratingDraft}
            disabled={isGeneratingDraft}
            onClick={async () => {
              if (!latestMsg || !onGenerateDraftReply) return;
              const draft = await onGenerateDraftReply({
                latestMessage: latestMsg,
                threadMessages: messages,
                styleSamples,
              });
              if (draft) onMessageDraftChange(draft);
            }}
          >
            Générer la réponse IA
          </Button>
          <Button type="primary" icon={<PlusOutlined />} style={{ borderRadius: 8 }}>
            Envoyer
          </Button>
        </div>
      </div>
      <Modal
        open={isThreadSummaryModalOpen}
        title="Résumé du thread"
        onCancel={() => setIsThreadSummaryModalOpen(false)}
        footer={null}
      >
        <Typography.Paragraph style={{ marginBottom: 0 }}>
          {threadSummary}
        </Typography.Paragraph>
      </Modal>
    </Drawer>
  );
};

export default ThreadDrawer;
